import { useState, useEffect, useRef } from 'react';
import imgBC from '../../assets/services/img.bc.jpg';
import imgSC from '../../assets/services/img.sc.jpg';
import imgASP from '../../assets/services/img.asp.jpg';
import img2PM3PM from '../../assets/services/img.2pm3pm.jpg';

interface ServiceFeature {
	title: string;
	description: string;
}

interface ServiceRow {
	title: string;
	imageAlt: string;
	imageSrc: string;
	features: ServiceFeature[];
}

export const Services = () => {
	const [selectedServiceIndex, setSelectedServiceIndex] = useState(0);
	const [isPaused, setIsPaused] = useState(false);
	const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Auto-rotate selected service every 3 seconds (only when not paused)
	useEffect(() => {
		if (isPaused) return;

		const interval = setInterval(() => {
			setSelectedServiceIndex((prevIndex) => (prevIndex + 1) % 4);
		}, 3000);

		return () => clearInterval(interval);
	}, [isPaused]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (pauseTimeoutRef.current) {
				clearTimeout(pauseTimeoutRef.current);
			}
		};
	}, []);

	// Handle card click - pause rotation for 10 seconds
	const handleCardClick = (index: number) => {
		setSelectedServiceIndex(index);
		
		// Clear any existing pause timeout
		if (pauseTimeoutRef.current) {
			clearTimeout(pauseTimeoutRef.current);
		}

		// Pause the rotation
		setIsPaused(true);

		// Resume after 10 seconds
		pauseTimeoutRef.current = setTimeout(() => {
			setIsPaused(false);
		}, 10000);
	};

	// Background colors for each service
	const backgroundColors = [
		'bg-green-50',  // Service 0
		'bg-blue-50', // Service 1
		'bg-pink-50',  // Service 2
		'bg-amber-50',  // Service 3
	];

	// Text colors for each service (matching the backgrounds)
	const textColors = [
		'text-gray-900',  // Service 0 - green background
		'text-gray-900',  // Service 1 - blue background
		'text-gray-900',  // Service 2 - pink background
		'text-gray-900',  // Service 3 - amber background
	];

	// Bullet point colors
	const bulletColors = [
		'text-green-600',  // Service 0
		'text-blue-600',   // Service 1
		'text-pink-600',   // Service 2
		'text-amber-600',  // Service 3
	];

	// Right column card background colors when selected
	const cardBgColors = [
		'bg-green-100',  // Service 0
		'bg-blue-100',   // Service 1
		'bg-pink-100',   // Service 2
		'bg-amber-100',  // Service 3
	];

	// Right column card text colors
	const cardTextColors = [
		'text-gray-900',  // Service 0
		'text-gray-900',  // Service 1
		'text-gray-900',  // Service 2
		'text-gray-900',  // Service 3
	];

	const serviceRows: ServiceRow[] = [
		{
			title: 'Breakfast Club – 8:00am to 9:00am',
			imageAlt: 'Breakfast Club',
			imageSrc: imgBC,
			features: [
				{
					title: 'Early Morning Care',
					description: 'A calm, welcoming start to the day for busy families'
				},
				{
					title: 'Relaxed Morning Routine',
					description: 'Time to chat with friends or enjoy quiet play before school.'
				},
				{
					title: 'Safe School Drop-Off',
					description: 'Children are personally walked to Mucklagh National School each morning.'
				},
				{
					title: 'Friendly, Familiar Environment',
					description: 'Run by our caring staff who know each child well.'
				},
				{
					title: 'Supports Working Parents',
					description: 'Reliable morning care that fits around family schedules.'
				},
				{
					title: 'Community Connection',
					description: 'Continuing our long-standing commitment.'
				}
			]
		},
		{
			title: 'Sessional Care – Morning ECCE Programme',
			imageAlt: 'Sessional Care',
			imageSrc: imgSC,
			features: [
				{
					title: 'Morning ECCE Sessions',
					description: 'Quality early learning through the Aistear and Siolta frameworks'
				},
				{
					title: 'Small Group Settings',
					description: 'Focused attention and a welcoming, family-style atmosphere'
				},
				{
					title: 'Play-Based Learning',
					description: 'Encouraging creativity, curiosity, and confidence.'
				},
				{
					title: 'Outdoor Play & Exploration',
					description: 'Daily outdoor time to support wellbeing and development.'
				},
				{
					title: 'Qualified, Caring Staff',
					description: 'Experienced early years educators dedicated to each child\'s growth.'
				},
				{
					title: 'Strong Home–School Links',
					description: 'Regular communication and updates for parents'
				},
				{
					title: 'Community-Focused Setting',
					description: 'Building friendships and a sense of belonging.'
				}
			]
		},
		{
			title: '2PM - 3PM Club',
			imageAlt: '2PM - 3PM Club',
			imageSrc: img2PM3PM,
			features: [
				{
					title: 'Convenient School Collection',
					description: 'Children are collected from Mucklagh National School at 2pm.'
				},
				{
					title: 'Flexible Afternoon Care',
					description: 'Supervised activities, play, and a safe environment until 3pm.'
				},
				{
					title: 'School Drop-Off Service',
					description: 'Children are walked back to Mucklagh National School for the 3pm collection.'
				},
				{
					title: 'Reduces Parent Pick-Ups',
					description: 'Parents with children finishing at different times only need one school collection.'
				},
				{
					title: 'Safe and Secure',
					description: 'Staff supervise children at all times during activities and the walk back to school.'
				},
				{
					title: 'Age-Appropriate Activities',
					description: 'Fun and engaging activities suited to the children\'s age group.'
				},
				{
					title: 'Community-Focused',
					description: 'Supports busy families while maintaining a safe, caring environment.'
				}
			]
		},
		{
			title: 'After-School Programme',
			imageAlt: 'After-School Programme',
			imageSrc: imgASP,
			features: [
				{
					title: 'Safe School Collection',
					description: 'Children are collected directly from Mucklagh National School each afternoon.'
				},
				{
					title: 'Two Age-Based Settings',
					description: 'Junior Infants to 2nd Class and 3rd to 6th Class, allowing children more space and age-appropriate activities.'
				},
				{
					title: 'Healthy Snack Provided',
					description: 'A nutritious pick-me-up after a busy school day.'
				},
				{
					title: 'Homework Support',
					description: 'Staff provide guidance and a quiet space for children to complete their homework.'
				},
				{
					title: 'Fun & Relaxed Environment',
					description: 'Time to unwind, play, and socialise with friends.'
				},
				{
					title: 'Creative & Outdoor Play',
					description: 'Plenty of outdoor space for physical movement and activities that encourage imagination and confidence.'
				},
				{
					title: 'Qualified, Caring Staff',
					description: 'Experienced educators ensuring a positive end to each child\'s day.'
				}
			]
		}
	];

	

	return (
		<div className="bg-[#e3f2fd]">
		<section id="services" className="py-16 px-4 sm:px-6 lg:px-8 min-h-screen">
		<div className="body-container w-full mb-5">
			<button className="button-offer mb-4">
				Our Services
			</button>
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
				<div>
					<h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-serif">
						What We Offer Your Family
					</h2>
				</div>
				<div className="flex items-start">
					<p className="text-gray-600 text-base md:text-lg font-['DM_Sans']">
						Our dedicated team provides consistency and care for every child, helping them feel at
						home while learning, playing, and growing alongside their friends. We're more than just
						a childcare service, we're a community where children and families belong.
					</p>
				</div>
			</div>
		</div>
			
			{/* Three Rows Section */}
			{/* <div className="max-w-7xl mx-auto space-y-10">
				{serviceRows.map((row, index) => {
					const isEven = index % 2 === 1; // index 1 (second row) is even
					// On mobile: image always first (order-1), content always second (order-2)
					// On desktop: alternate - odd rows (image left), even rows (content left)
					const imageOrder = isEven ?  'order-1 lg:order-1' : 'order-1 lg:order-2';
					const contentOrder = isEven ? 'order-2 lg:order-2' : 'order-2 lg:order-1';
					// For odd rows, make image smaller (2/5 width), content larger (3/5 width)
					// Even rows use same ratio but swapped positions
					const imageColSpan = 'lg:col-span-2';
					const contentColSpan = 'lg:col-span-3';

					// Card colors: green (index 0), blue (index 1), pink (index 2)
					const cardColors = [
						{ bg: 'bg-green-50', header: 'bg-green-100', text: 'text-gray-900', textSecondary: 'text-gray-700' }, // Green
						{ bg: 'bg-blue-50', header: 'bg-blue-100', text: 'text-gray-900', textSecondary: 'text-gray-700' }, // Blue
						{ bg: 'bg-pink-50', header: 'bg-pink-100', text: 'text-gray-900', textSecondary: 'text-gray-700' }  // Pink
					];
					const colors = cardColors[index % 2];

					return (
						<div key={index} className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-5 items-stretch">
							<div className={`${imageOrder} ${imageColSpan} h-[300px] sm:h-[400px] md:h-[500px] lg:h-full lg:min-h-0`}>
								<div className="relative w-full h-full rounded-lg overflow-hidden">
									<img
										src={row.imageSrc}
										alt={row.imageAlt}
										className="w-full h-full object-cover"
									/>
								</div>
							</div>

							<div className={`${contentOrder} ${contentColSpan} ${colors.bg} rounded-lg p-8 lg:p-12 lg:h-full flex flex-col`}>
								<div className={`py-2 mb-4`}>
									<h2 className={`text-3xl font-serif ${colors.text} font-bold`}>
										{row.title}
									</h2>
								</div>
								<ul className={`space-y-6 ${colors.textSecondary}`}>
									{row.features.map((feature, featureIndex) => (
										<li key={featureIndex}>
											<p className={`font-bold ${colors.text} mb-1`}>{feature.title}</p>
											<p className={`text-base  leading-relaxed font-['DM_Sans'] ${colors.textSecondary}`}>{feature.description}</p>
										</li>
									))}
								</ul>
							</div>
						</div>
					);
				})}
			</div> */}

			{/* New Interactive Service Section */}
			<div className="body-container mt-16 bg-white rounded-lg p-8 lg:p-12">
				<div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
					{/* Left Column - Main Article */}
					<div className="lg:col-span-6 grid grid-cols-1 gap-6">
						{/* Image Section - 50% */}
						<div className="relative w-full h-[300px] rounded-lg overflow-hidden">
							<img
								src={serviceRows[selectedServiceIndex].imageSrc}
								alt={serviceRows[selectedServiceIndex].imageAlt}
								className="w-full h-full object-cover"
							/>
						</div>
						
						{/* Content Section - 50% */}
						<div className="flex flex-col transition-colors duration-700 ease-in-out">
							<h2 className={`text-3xl font-bold ${textColors[selectedServiceIndex]} mb-6 font-['Quicksand'] transition-colors duration-700 ease-in-out`}>
								{serviceRows[selectedServiceIndex].title}
							</h2>
							
							{/* Bullet Points */}
							<ul className="space-y-4">
								{serviceRows[selectedServiceIndex].features.map((feature, index) => (
									<li key={index} className="flex items-start">
										<span className={`${bulletColors[selectedServiceIndex]} mr-3 mt-1 transition-colors duration-700 ease-in-out`}>•</span>
										<span className={`${textColors[selectedServiceIndex]} text-base leading-relaxed font-['DM_Sans'] transition-colors duration-700 ease-in-out`}>
											{feature.title}: {feature.description}
										</span>
									</li>
								))}
							</ul>
						</div>
					</div>

					{/* Right Column - Service Cards (4 items) */}
					<div className="lg:col-span-4 flex flex-col gap-3">
						{serviceRows.slice(0, 4).map((row, index) => (
							<div
								key={index}
								onClick={() => handleCardClick(index)}
								className={`cursor-pointer p-4 rounded-lg transition-all duration-700 ease-in-out ${
									selectedServiceIndex === index ? `${cardBgColors[index]}` : 'bg-transparent hover:bg-gray-100'
								}`}
							>
								<div className="flex gap-4">
									{/* Thumbnail Image */}
									<div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden">
										<img
											src={row.imageSrc}
											alt={row.imageAlt}
											className="w-full h-full object-cover"
										/>
									</div>
									
									{/* Content */}
									<div className="flex-1 flex flex-col justify-center">
										<h3 className={`${selectedServiceIndex === index ? cardTextColors[index] : 'text-gray-700'} text-lg font-semibold font-['Quicksand'] transition-colors duration-700 ease-in-out`}>
											{row.title}
										</h3>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
		</div>
	);
};

export default Services;